import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Progress, message, Button, Modal } from "antd";
import jwtDecode from "jwt-decode";
import { SyncOutlined } from "@ant-design/icons";
import {
  cancelDataLoading,
  fetchShopifyDataStatus,
} from "src/services/PipelineAPI";
import { getApiKey, getTenantId } from "src/utils/getters";

interface DecodedToken {
  user_id: string;
  tenant_id: string;
  role_id: string;
  api_key: string;
  company_name: string;
}

const delay = (ms: number): Promise<void> =>
  new Promise(resolve => setTimeout(resolve, ms));

const MagicScreen: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  if (token) {
    try {
      const decodedToken = jwtDecode<DecodedToken>(token);
      localStorage.setItem("user_id", decodedToken.user_id);
      localStorage.setItem("tenant_id", decodedToken.tenant_id);
      localStorage.setItem("role_id", decodedToken.role_id);
      localStorage.setItem("api_key", decodedToken.api_key);
      localStorage.setItem("company_name", decodedToken.company_name);
      const newUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, newUrl);
    } catch {
      console.error("Error decoding token:", token);
    }
  }

  const navigate = useNavigate();
  const api_key = localStorage.getItem("api_key")?.trim();
  const user_id = localStorage.getItem("user_id");
  const tenant_id = localStorage.getItem("tenant_id");
  const role_id = localStorage.getItem("role_id");
  const [fetchData, setFetchData] = useState(true);
  const [progress, setProgress] = useState(0);
  const [showMagicMessage, setShowMagicMessage] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [, setFailedCount] = useState(0);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const pollingInterval = 20000;
  const fetchDataInterval = useRef<NodeJS.Timeout | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [estimatedTime, setEstimatedTime] = useState(
    "Estimated time: Calculating..."
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Add beforeunload warning effect that only triggers if progress > 0
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (progress > 0) {
        e.preventDefault();
        e.returnValue =
          "Are you sure you want to leave? Your changes may not be saved.";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [progress]);

  const showCancleModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleCancleDataLoading = async () => {
    setIsLoading(true);
    setFetchData(false);

    const tenant_id = getTenantId();
    const api_key = getApiKey();

    if (!tenant_id || !api_key) {
      message.error("Missing credentials. Please log in again.");
      navigate("/log-in");
      return;
    }

    if (fetchDataInterval.current) {
      clearInterval(fetchDataInterval.current);
      fetchDataInterval.current = null;
    }

    try {
      const response = await cancelDataLoading(tenant_id, api_key);

      if (response.status === 401) {
        navigate("/log-in");
      } else if (response.status === 200) {
        localStorage.setItem("Shopify_connected", "false");
        navigate("/welcome-to-datatram/connect-sources" , {
          state: { currentStep: 1 },
        });
      }
    } catch (e) {
      console.error("Error cancelling data load:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDataFromBackend = useCallback(async () => {
    if (!fetchData) {
      return;
    }

    const tenant_id = getTenantId();
    const api_key = getApiKey();

    if (!tenant_id || !api_key) {
      message.error("Missing credentials. Please log in again.");
      navigate("/log-in");
      return;
    }

    try {
      const data = await fetchShopifyDataStatus(tenant_id, api_key);
      // ... rest of logic
    } catch (e) {
      console.error("Fetch error:", e);
      message.error("Failed to fetch data");
    }
  }, [fetchData, isFadingOut]);

  const clickOK = () => {
    navigate("/welcome-to-datatram/connect-sources", {
      state: { currentStep: 1 },
    });
  };

  useEffect(() => {
    fetchDataFromBackend();
    fetchDataInterval.current = setInterval(
      fetchDataFromBackend,
      pollingInterval
    );

    return () => {
      if (fetchDataInterval.current) {
        clearInterval(fetchDataInterval.current);
      }
    };
  }, [fetchDataFromBackend, pollingInterval]);

  useEffect(() => {
    if (showMagicMessage) {
      setTimeout(() => {
        navigate("/dashboard", {
          state: { tenant_id, user_id, role_id },
        });
      }, 4000);
    }
  }, [showMagicMessage, navigate, tenant_id, user_id, role_id]);

  return (
    <div className="loading-screen">
      <div className="loading-screen-container">
        <div className="dt-logo-loading-scr">
          <img src="/images/dt-logo-white.svg" alt="Logo" />
        </div>

        <div className="loading-content">
          {showMagicMessage ? (
            <p className="loading-screen-heading now-see-the-magic">
              Now see the magic
            </p>
          ) : showErrorMessage ? (
            <div className="error-container-data-fetching fade-in">
              <h2 className="error-heading">Data Fetching Failed</h2>
              <div className="error-content">
                <img
                  src="/Icons/alert-icon.svg"
                  alt="Error Icon"
                  className="error-icon-data-fetching"
                />
                <span className="error-text-data-fetching">
                  We apologise for the inconvenience. DataTram support team will
                  investigate this on priority and will let you know when it is
                  resolved.
                </span>
              </div>
              <button className="error-ok-button" onClick={clickOK}>
                OK
              </button>
            </div>
          ) : (
            <>
              <p
                className={`loading-screen-heading ${
                  isFadingOut ? "fade-out" : ""
                }`}
              >
                DataTram is working its magic
              </p>
              <Progress
                percent={progress}
                status={progress < 100 ? "active" : "success"}
                strokeColor="#3ec3f0"
                trailColor="white"
                strokeWidth={30}
                className={`custom-progress ${isFadingOut ? "fade-out" : ""}`}
                style={{ width: "400px" }}
              />
              <p
                style={{
                  color: "white",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginTop: "10px",
                }}
              >
                {estimatedTime}
              </p>
              <Button
                type="text"
                className="cancel-btnMagicScreen"
                style={{
                  color: "white",
                  textDecoration: "underline",
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontSize: "16px",
                }}
                onClick={showCancleModal}
              >
                Cancel
              </Button>

              <Modal
                className="logout-modal-content"
                centered
                open={modalOpen}
                footer={null}
                onCancel={handleCloseModal}
              >
                <div className="logout-modal-wrapper">
                  <div className="logout-modal-header">
                    <img
                      src="/Icons/cancleIcon.svg"
                      className="logout-Icon"
                      alt="Logout Icon"
                    />
                    <label className="popup-msg-content">
                      Aborting Progress?
                    </label>
                  </div>
                  <div className="logout-modal-body">
                    <label>
                      Are you sure you want to cancel? You will loose your
                      progress and will have to start again.
                    </label>
                  </div>
                  <div className="logout-modal-footer">
                    <Button
                      key="confirm"
                      className="red-btn"
                      onClick={handleCancleDataLoading}
                      loading={isLoading}
                      icon={isLoading ? <SyncOutlined spin /> : null}
                      disabled={isLoading ? true : false}
                    >
                      {isLoading ? "Processing" : " Yes"}
                    </Button>
                    <Button
                      key="cancel"
                      className="grey-btn"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Modal>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MagicScreen;
